import { Link } from "react-router-dom";
import { Activity, ArrowUpRight, Baby, FlaskConical, HeartPulse, Scale, Stethoscope } from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionTag from "@/components/SectionTag";
import { SERVICES } from "@/data/site";

const ICONS = { Stethoscope, Baby, Activity, Scale, HeartPulse, FlaskConical };

export default function ServicesGrid() {
  return (
    <section data-testid="services-section" className="bg-slate-50/70">
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-12">
        <Reveal className="max-w-2xl">
          <SectionTag number="02" label="Our Services" />
          <h2 className="mt-5 font-display text-2xl font-bold tracking-tight text-brand-navy sm:text-3xl lg:text-4xl">
            Five ways we care for{" "}
            <span className="font-serif italic text-brand-red">you and yours</span>
          </h2>
          <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
            Compassionate, personalized care for individuals and families—built
            on long-term relationships, not rushed transactions.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((service, i) => {
            const Icon = ICONS[service.icon];
            return (
              <Reveal key={service.slug} delay={i * 0.07}>
                <Link
                  to={`/services/${service.slug}`}
                  data-testid={`service-card-${service.slug}`}
                  className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white p-8 shadow-soft transition-[box-shadow,transform] duration-300 hover:-translate-y-1.5 hover:shadow-lift"
                >
                  <span className="absolute inset-x-0 top-0 h-1 origin-left scale-x-0 bg-brand-red transition-transform duration-500 group-hover:scale-x-100" />
                  <div className="flex items-center justify-between">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-redlight transition-colors duration-300 group-hover:bg-brand-red">
                      <Icon className="h-5 w-5 text-brand-red transition-colors duration-300 group-hover:text-white" />
                    </span>
                    <span className="font-mono text-xs tracking-[0.3em] text-slate-300">
                      {service.number}
                    </span>
                  </div>
                  <h3 className="mt-6 font-display text-xl font-semibold text-brand-navy">
                    {service.name}
                  </h3>
                  <p className="mt-2.5 flex-1 text-sm leading-relaxed text-slate-500">
                    {service.short}
                  </p>
                  <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-red">
                    Learn more
                    <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </Link>
              </Reveal>
            );
          })}
          <Reveal delay={0.35}>
            <div className="flex h-full flex-col justify-between rounded-3xl bg-brand-navy p-8">
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-brand-red">
                  Same-Day Care
                </p>
                <h3 className="mt-4 font-display text-xl font-semibold text-white">
                  Need to be seen today?
                </h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-300">
                  Same-day appointments are available by phone, Wednesday
                  through Sunday.
                </p>
              </div>
              <a
                href="tel:+19283277528"
                data-testid="services-call-card-link"
                className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white transition-colors duration-200 hover:text-brand-red"
              >
                Call (928) 327-7528
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
